import React, { useEffect, useState } from 'react';
import { Subject, filter, takeUntil } from 'rxjs';

import {
  Container,
  BitSourceType,
  XpSourceType,
  i18n,
  DataService,
} from '../../../../symphony';

import { SlActions } from '../../services/ActionsService/sl-actions';
import { SlDataService } from '../../services/sl-data.service';
import { SlLEContext } from '../../global-constants';
import {
  ActionType,
  RequestType,
  UserSettingKey,
} from '../../global-interface';
import { useEnterKeyPress } from '../../utils/useEnterKeyPress';
import { SlFirstLessonCompleteCelebration } from '../../../../shared/public/SlCompleteCelebration/SlFirstLessonCompleteCelebration/SlFirstLessonCompleteCelebration';
import { AnimationId, SlCompleteCelebration } from '../../../../shared/public/SlCompleteCelebration/SlCompleteCelebration';
import { MaterialType } from '../../../../api/private/learn-engine-api/learn-engine-api.interface';

export function LessonCompleteCelebration(): JSX.Element {
  const [isFirstLessonComplete, setIsFirstLessonComplete] = useState<boolean>(null);

  const { actions$ } = Container.take(SlLEContext, SlActions);
  const {
    lesson$, userSettings$, lessonOrderInCourse$, lessonStatus,
  } = Container.take(SlLEContext, SlDataService);

  const { xpSourcesList$, bitSourcesList$ } = Container.take(
    'global',
    DataService,
  );

  const { typeId, title } = lesson$.value.materialInfo.header;

  const isJustCompleted = !lessonStatus?.isCompleted;

  const isBooster = typeId === MaterialType.Booster;

  const isSkipAhead = typeId === MaterialType.SkipAhead;

  const xp = isJustCompleted
    ? xpSourcesList$.value?.xpSources?.find(
      (src) => src.xpSource === (isBooster
        ? XpSourceType.BoosterComplete
        : isSkipAhead
          ? XpSourceType.SkipAheadComplete
          : XpSourceType.LessonComplete),
    )?.xp
    : null;

  const bits = isJustCompleted
    ? bitSourcesList$.value?.find(
      (src) => src.id === (isBooster
        ? BitSourceType.BoosterLessonComplete
        : BitSourceType.LessonComplete),
    )?.price
    : null;

  useEffect(() => {
    if (isJustCompleted && lessonOrderInCourse$.value === 1 && !isBooster) {
      const unsubscriber = new Subject<void>();

      userSettings$
        .pipe(
          takeUntil(unsubscriber),
          filter(
            (userSettings) => userSettings.length
              && !userSettings.some(
                ({ key, value }) => key
                  === UserSettingKey.IntroBitsAndXpsShown
                  && value === 'true',
              ),
          ),
        )
        .subscribe(() => {
          setIsFirstLessonComplete(true);
          actions$.next({
            type: ActionType.OnFirstLessonCompleteCelebrationMount,
            requestType: RequestType.Parallel,
          });
        });

      return () => {
        unsubscriber.next();
        unsubscriber.complete();
      };
    }
    return () => { };
  }, []);

  const onContinueCallback = () => {
    actions$.next({
      type: ActionType.OnLessonCompletedModalContinueClick,
      requestType: RequestType.Parallel,
    });
  };

  useEnterKeyPress(onContinueCallback);

  const onFirstLessonContinueCallBack = () => {
    actions$.next({
      type: ActionType.OnFirstLessonCelebrationContinueClick,
      requestType: RequestType.Parallel,
    });
  };

  const getAnimationId = (): AnimationId => ({
    [MaterialType.Lesson]: AnimationId.Celebration,
    [MaterialType.ModuleQuiz]: AnimationId.Celebration,
    [MaterialType.Booster]: AnimationId.Booster,
  }[typeId]);

  const getTitle = (): string => ({
    [MaterialType.Lesson]: i18n.t('web-le.lesson-complete-celebration-title'),
    [MaterialType.ModuleQuiz]: i18n.t('web-le.quiz-complete-celebration-title'),
    [MaterialType.Booster]: i18n.t('web-le.booster-complete-celebration-title'),
    [MaterialType.SkipAhead]: i18n.t('reward.title.skip_ahead'),
  }[typeId]);

  const getDescription = (): string => ({
    [MaterialType.Lesson]: i18n.t('web-le.lesson-complete-celebration-description', { title }),
    [MaterialType.ModuleQuiz]: i18n.t('web-le.quiz-complete-celebration-description'),
    [MaterialType.Booster]: i18n.t('web-le.booster-complete-celebration-description'),
    [MaterialType.SkipAhead]: i18n.t('reward.body.skip_ahead'),
  }[typeId]);

  return isFirstLessonComplete ? (
    <SlFirstLessonCompleteCelebration
      xp={xp}
      bits={bits}
      onContinueClick={onFirstLessonContinueCallBack}
      lessonName={title}
    />
  ) : (
    <SlCompleteCelebration
      animationId={getAnimationId()}
      title={getTitle()}
      description={getDescription()}
      xp={xp}
      bits={bits}
      buttonText={i18n.t('web-le.complete-lesson-button')}
      onContinue={onContinueCallback}
    />
  );
}
